import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { mediaUrl } from "@/lib/api";
import { captureProof } from "@/lib/proof";
import { LENGTH_PRESETS, dpiOf } from "@/lib/gangsheet";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import { useGangSheet } from "@/components/shirts/gang/useGangSheet";
import SheetStage from "@/components/shirts/gang/SheetStage";
import { useSimpleLine } from "./useSimpleLine";
import { FileCheck, NoteField, PriceBar, QtyPicker, ReadyBy, Section, btnChip, input, money } from "./Fields";

const WALL = "#eef0f3";

/** One design, its print width and how many copies — the DTF by-size rows (5.3 "DTF Transfers"). */
function PieceRow({ g, sheet }) {
    return (
        <div className="flex items-center gap-3 border border-ink p-2" data-testid={`piece-${g.fileId}`}>
            <img src={g.url} alt="" className="h-14 w-14 shrink-0 border border-line object-contain" />
            <div className="min-w-0 flex-1">
                <p className="truncate text-[16px]">{g.name || "design"}</p>
                <label className="mt-1 flex items-center gap-2 text-[14px] text-muted-ink">
                    <input type="number" inputMode="decimal" step="0.25" min="0.5" max={sheet.film.printable_width_inches} value={g.wIn}
                        onChange={(e) => sheet.setGroupWidth(g.fileId, Number(e.target.value) || 0)} className={`${input} w-20`} aria-label="Width in inches" data-testid={`piece-width-${g.fileId}`} />
                    in wide × {g.hIn}"
                </label>
            </div>
            <div className="flex items-center gap-1">
                <button type="button" onClick={() => sheet.setGroupQty(g.fileId, g.uids.length - 1)} className="h-11 w-11 border border-ink text-[20px]" aria-label="Fewer" data-testid={`piece-minus-${g.fileId}`}>−</button>
                <span className="w-8 text-center text-[16px] font-semibold" data-testid={`piece-qty-${g.fileId}`}>{g.uids.length}</span>
                <button type="button" onClick={() => sheet.setGroupQty(g.fileId, g.uids.length + 1)} className="h-11 w-11 border border-ink text-[20px]" aria-label="More" data-testid={`piece-plus-${g.fileId}`}>+</button>
            </div>
            <button type="button" onClick={() => sheet.removeGroup(g.fileId)} className="min-h-[44px] underline" data-testid={`piece-remove-${g.fileId}`}>Remove</button>
        </div>
    );
}

/** Gang Sheet (the builder inline) and DTF Transfers (the same builder in pieces mode). */
export default function SheetBody({ mode, store, cleanupFee, placeholder, ready, dtf }) {
    const sheet = useGangSheet(store?.slug);
    const line = useSimpleLine({ store, cleanupFee });
    const [designing, setDesigning] = useState(false);
    const [busy, setBusy] = useState(false);
    const stageRef = useRef(null);
    const fileRef = useRef(null);
    const pieces = mode === "transfers";

    const addFiles = async (files) => {
        const list = Array.from(files || []);
        if (!list.length) return;
        setBusy(true);
        try {
            for (const file of list) {
                const url = URL.createObjectURL(file);
                const { w, h } = await new Promise((res) => {
                    const img = new Image();
                    img.onload = () => res({ w: img.naturalWidth || 900, h: img.naturalHeight || 900 });
                    img.onerror = () => res({ w: 900, h: 900 });
                    img.src = url;
                });
                const rec = await uploadArtwork(file);
                const ratio = w / h;
                const wIn = Math.min(sheet.film.printable_width_inches, Math.max(1, Math.round((w / 300) * 4) / 4));
                sheet.addItem({ fileId: rec.id, file: rec, url, name: file.name, ratio, px: { w, h }, wIn, hIn: Math.round((wIn / ratio) * 100) / 100 });
            }
        } catch { toast.error("Upload failed. Try again."); } finally { setBusy(false); }
    };

    const low = sheet.groups.filter((g) => (dpiOf(g) || 0) < 150);
    const check = sheet.groups.length ? { ok: low.length === 0 } : null;
    useEffect(() => { if (check && !check.ok) line.setCleanup(true); if (check?.ok) line.setCleanup(false); }, [check?.ok]); // eslint-disable-line react-hooks/exhaustive-deps
    const q = sheet.quote;
    const film = sheet.film;
    const errors = sheet.items.length
        ? [...(q?.errors || []), sheet.conflicts.length ? "Fix overlapping images" : null, sheet.oob.length ? "Move images inside the sheet" : null].filter(Boolean)
        : ["Upload your designs"];
    const total = (q?.line_total || 0) + line.fee;
    const pieceCount = sheet.items.length;

    const build = async () => {
        const proof = await captureProof(stageRef.current, pieces ? "dtf-transfers" : "dtf-gang-sheet", WALL);
        return {
            product_slug: pieces ? "dtf-transfers" : "dtf-gang-sheet",
            product_name: pieces ? "DTF Transfers" : "DTF Gang Sheet",
            image: proof ? mediaUrl(proof.url) : "",
            quantity: q.quantity, unit_price: q.unit_price, line_total: q.line_total,
            summary: pieces
                ? `${pieceCount} transfer${pieceCount === 1 ? "" : "s"} · ${q.linear_feet} linear ft`
                : `${film.width_inches}" × ${q.billed_length_in}" · ${q.linear_feet} linear ft · ${pieceCount} images`,
            store,
            selections: {
                kind: "dtf", store_slug: store?.slug, length_in: sheet.lengthIn, quantity: sheet.sheets, film_id: sheet.filmId, film_name: film.name, ready_in: dtf?.ready_in || "same_day",
                width_in: film.width_inches, spacing_in: film.min_spacing_inches, printable_width_in: film.printable_width_inches,
                coverage_pct: sheet.cover, pieces_mode: pieces,
                layout: sheet.items.map((it) => ({ file_id: it.file?.id, name: it.name, x: it.x, y: it.y, w: it.wIn, h: it.hIn, rot: it.rot || 0, dpi: it.px ? Math.floor(it.px.w / it.wIn) : null })),
                design_json: { items: sheet.items.map(({ url, ...rest }) => rest), length_mode: sheet.lengthMode, sheets: sheet.sheets },
            },
            proof, artwork: sheet.groups.map((g) => g.file),
        };
    };

    const rate = q?.rate_per_foot ?? dtf?.price_per_linear_foot ?? 5;
    const lengthLabel = useMemo(() => `${(sheet.lengthIn / 12).toFixed(sheet.lengthIn % 12 ? 1 : 0)} ft`, [sheet.lengthIn]);

    return (
        <>
            <Section testid="design-section">
                <div className="relative h-[110vw] max-h-[640px] w-full border border-line" style={{ background: WALL }} data-testid="sheet-stage-wrap">
                    <div ref={stageRef} className="h-full w-full">
                        <SheetStage items={sheet.items} selected={sheet.selected} setSelected={sheet.setSelected} lengthIn={sheet.lengthIn} zoom={1} fitPad={{ x: 120, y: 120 }}
                            conflicts={sheet.conflicts} oob={sheet.oob} film={film}
                            onDragStart={() => sheet.commit((p) => p)} onLiveChange={(uid, patch) => sheet.updateItem(uid, patch, { history: false })} />
                    </div>
                    {!sheet.items.length && (
                        <button type="button" onClick={() => fileRef.current?.click()} className="absolute inset-x-6 bottom-6 flex min-h-[48px] items-center justify-center border border-ink bg-page text-[16px] font-semibold" data-testid="upload-file">
                            {busy ? "Uploading…" : "Upload file"}
                        </button>
                    )}
                </div>
                <input ref={fileRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="upload-input" />
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[16px]">
                    {sheet.items.length > 0 && <button type="button" onClick={() => fileRef.current?.click()} className="min-h-[44px] underline" data-testid="add-design">{pieces ? "Add another design" : "Add art"}</button>}
                    {!pieces && <button type="button" onClick={() => setDesigning((v) => !v)} className="min-h-[44px] underline" data-testid="design-it">{designing ? "Close designer" : "Design it"}</button>}
                </div>
                {designing && sheet.selected && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="inline-designer">
                        <button type="button" onClick={() => sheet.rotateItem(sheet.selected)} className={btnChip(false)} data-testid="rotate-item">Rotate</button>
                        <button type="button" onClick={() => sheet.duplicateItem(sheet.selected)} className={btnChip(false)} data-testid="duplicate-item">Duplicate</button>
                        <button type="button" onClick={() => sheet.removeItem(sheet.selected)} className={btnChip(false)} data-testid="delete-item">Delete</button>
                        <button type="button" onClick={sheet.tidy} className={btnChip(false)} data-testid="tidy-sheet">Re-nest</button>
                    </div>
                )}
                {designing && !sheet.selected && sheet.items.length > 0 && <p className="mt-2 text-[14px] text-muted-ink">Tap a design on the sheet to rotate, duplicate or delete it. Drag to move.</p>}
                <FileCheck check={check} cleanup={line.cleanup} setCleanup={line.setCleanup} fee={cleanupFee} />
            </Section>
            <Section title={pieces ? "Your designs" : "Sheet length"} testid="size-section">
                {pieces ? (
                    sheet.groups.length ? <div className="flex flex-col gap-2">{sheet.groups.map((g) => <PieceRow key={g.fileId} g={g} sheet={sheet} />)}</div>
                        : <p className="text-[16px] text-muted-ink">Upload a design, then set its width and how many you need.</p>
                ) : (
                    <>
                        <div className="grid grid-cols-3 gap-2" data-testid="length-picker">
                            <button type="button" onClick={() => sheet.setLengthMode("auto")} className={btnChip(sheet.lengthMode === "auto")} data-testid="length-auto">Auto · {lengthLabel}</button>
                            {LENGTH_PRESETS.map((n) => <button key={n} type="button" onClick={() => sheet.setLengthMode(n)} className={btnChip(sheet.lengthMode === n)} data-testid={`length-${n}`}>{n / 12} ft</button>)}
                        </div>
                        <p className="mt-3 text-[16px]">Identical sheets</p>
                        <div className="mt-2"><QtyPicker presets={[1, 2, 5]} value={sheet.sheets} onChange={sheet.setSheets} testid="sheets" /></div>
                    </>
                )}
            </Section>
            <NoteField value={line.note} onChange={line.setNote} placeholder={placeholder} />
            <ReadyBy store={store} readyIn={dtf?.ready_in || "same_day"} extraDays={line.extraDays} fallback={ready} needBy={line.needBy} setNeedBy={line.setNeedBy} />
            <PriceBar total={total} busy={line.busy || busy} errors={errors}
                note={`${film.width_inches}" film · ${q?.linear_feet || 0} linear ft at ${money(rate)} a ft${sheet.sheets > 1 ? ` × ${sheet.sheets} sheets` : ""}${line.fee ? ` + ${money(line.fee)} cleanup` : ""}`}
                onPay={() => line.submit(build, "pay")} onAdd={() => line.submit(build, "cart")} />
        </>
    );
}
